import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  useGetIllustListMasters,
  useGetIllustListUnusedlist,
  usePostIllustListSearch,
} from '../../../../api/illust-list/illust-list';
import { IllustListSearchConditions } from '../../../../api/model';
import { useDefaultOptions } from '../../../../hooks/api';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import ErrorComponent from '../../../atoms/ErrorComponent';
import NoDataComponent from '../../../atoms/NoDataComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import ErrorModal from '../../../modules/ErrorModal';
import OkModal from '../../../modules/OkModal';
import SlideToggle from '../../../modules/SlideToggle';
import SpinnerModal from '../../../modules/SpinnerModal';
import IllustList from './IllustList';
import SearchConditions from './SearchConditions';

/**
 * タブタイトル
 * @returns
 */
function Title() {
  // 言語情報
  const { t } = useTranslation();
  return (
    <Helmet>
      <title>{t('CMS-POC|イラスト一覧')}</title>
    </Helmet>
  );
}

/**
 * イラスト一覧
 * @returns
 */
function IndexPage() {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // データ取得のオプション
  const defaultOptions = useDefaultOptions(language);

  // 検索条件に利用するマスターデータの取得
  const {
    data: mastersResponse,
    isLoading: mastersIsLoading,
    isError: mastersIsError,
    error: mastersError,
  } = useGetIllustListMasters(defaultOptions);

  // 不要イラスト削除リスト取得
  const { data: unusedList, refetch: unusedRefetch } = useGetIllustListUnusedlist(defaultOptions);

  // イラスト検索 API
  const [recOverMessage, setRecOverMessage] = useState('');
  const [displayError, setDisplayError] = useState(false);
  const { mutateAsync: searchAsync, isLoading, data, isError, error } = usePostIllustListSearch(defaultOptions);
  useEffect(() => {
    setDisplayError(isError);
  }, [isError]);

  // 初期検索条件（セッションストレージに保存されている）
  const [defaultConditions] = useState<IllustListSearchConditions>(
    sessionStorageUtils.get<IllustListSearchConditions>('Illusts.SearchConditions', {})
  );

  // 検索条件
  const refConditions = useRef(sessionStorageUtils.get<IllustListSearchConditions>('Illusts.SearchConditions', {}));

  // 検索条件を表示
  const [openConditions, setOpenConditions] = useState(true);

  // データ取得中
  if (mastersIsLoading) {
    return (
      <div id="main">
        <Title />
        <WaitComponent />
      </div>
    );
  }

  // エラー時
  if (mastersIsError) {
    return (
      <div id="main">
        <Title />
        <ErrorComponent error={mastersError} />
      </div>
    );
  }

  // データが取得できなかった場合
  if (mastersResponse == null) {
    return (
      <div id="main">
        <Title />
        <NoDataComponent />
      </div>
    );
  }

  // データがエラーなく取得できた場合
  return (
    <div id="main">
      <Title />
      <section>
        <h3 className="trig">
          <span>
            {t('データ検索')}
            <button type="button" className="text-button" onClick={() => setOpenConditions((pre) => !pre)}>
              <i className={`fa-solid ${openConditions ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
            </button>
          </span>
        </h3>
        <SlideToggle open={openConditions}>
          <SearchConditions
            masters={mastersResponse?.data}
            defaultValues={defaultConditions}
            onSubmit={async (next) => {
              await searchAsync(
                { data: next },
                {
                  onSuccess: (response) => {
                    // API アクセスが成功した場合
                    sessionStorageUtils.set('Illusts.SearchConditions', next);
                    refConditions.current = next;
                    // レコード件数
                    if (response?.data.record_count !== null && Number(response?.data.record_count) > 10000) {
                      const msg = t(
                        '検索結果が10,000件を超たため、検索処理を中止しました。検索条件を見直して再度検索を実行して下さい。'
                      );
                      setRecOverMessage(msg);
                    }
                  },
                }
              );
            }}
          />
        </SlideToggle>
        <div className="master-navi gantt mt10 mb5">
          <h3 className="mb0">
            <span>{t('イラスト一覧')}</span>
          </h3>
          <p style={{ marginBlockStart: '2em', marginBlockEnd: '0px', marginInlineEnd: '5px' }}>
            {data?.data.record_count === undefined ? '0' : Number(data?.data.record_count).toLocaleString()}件
          </p>
        </div>
        <IllustList data={data} productionNos={unusedList?.data.production_nos} refetch={unusedRefetch} />
      </section>
      {/* 待ちモーダル */}
      {isLoading && <SpinnerModal />}
      {/* 一覧エラー */}
      {displayError && <ErrorModal error={error} onClickOk={() => setDisplayError(false)} />}
      {recOverMessage !== '' && <OkModal onClickOk={() => setRecOverMessage('')}>{recOverMessage}</OkModal>}
    </div>
  );
}

export default IndexPage;
